import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import {
  CreateProofreadingRule,
  ProofreadingRuleType,
} from '../../../services/types/proofreading-types';
import { RuleTypeSelector } from './RuleTypeSelector';

interface RuleFormProps {
  initialValues?: Partial<CreateProofreadingRule>;
  firmId: string;
  onSubmit: (rule: CreateProofreadingRule) => void;
  onCancel: () => void;
}

const formSchema = z.object({
  title: z
    .string()
    .min(3, 'Title must be at least 3 characters.')
    .max(100, 'Title cannot exceed 100 characters.'),
  description: z
    .string()
    .min(10, 'Description must be at least 10 characters.')
    .max(500, 'Description cannot exceed 500 characters.'),
  prompt: z.string().min(20, 'Prompt must be at least 20 characters.'),
  rule_type: z.enum([
    ProofreadingRuleType.GRAMMAR,
    ProofreadingRuleType.LEGAL,
    ProofreadingRuleType.CONTENT,
    ProofreadingRuleType.FORMATTING,
    ProofreadingRuleType.STYLE,
  ]),
  enabled: z.boolean().default(true).optional(),
  firm: z.string(),
});

export const RuleForm: React.FC<RuleFormProps> = ({
  initialValues,
  firmId,
  onSubmit,
  onCancel,
}) => {
  const isEditing = !!initialValues?.title;

  const form = useForm<CreateProofreadingRule>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: initialValues?.title || '',
      description: initialValues?.description || '',
      prompt: initialValues?.prompt || '',
      rule_type: initialValues?.rule_type || ProofreadingRuleType.GRAMMAR,
      enabled:
        initialValues?.enabled !== undefined ? initialValues.enabled : true,
      firm: firmId,
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...values,
        firm: firmId,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) return 'Saving...';
    return isEditing ? 'Update Rule' : 'Create Rule';
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Rule Title</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter rule title" />
              </FormControl>
              <FormDescription>A concise name for the rule.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter rule description"
                  rows={3}
                />
              </FormControl>
              <FormDescription>
                A brief explanation of what this rule checks for.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="prompt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>AI Prompt</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter the AI prompt that will be used to check the document"
                  rows={5}
                />
              </FormControl>
              <FormDescription>
                The instructions given to the AI when evaluating this rule. Be
                specific and clear.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rule_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Rule Type</FormLabel>
              <FormControl>
                <RuleTypeSelector
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Enabled</FormLabel>
                <FormDescription>
                  When enabled, this rule will be included in proofreading jobs.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {getSubmitButtonText()}
          </Button>
        </div>
      </form>
    </Form>
  );
};
